import { IntlProvider, type CustomFormats } from "react-intl";
import { useMemo } from "react";
import { useSalon, useSalonDateFnsLocale } from "@/hooks/me/use-salon";

const CLOCK: Intl.DateTimeFormatOptions = { timeStyle: "short" };
const DAY: Intl.DateTimeFormatOptions = { weekday: "short", day: "numeric", month: "short" };

// The app's only formatting options: components format with react-intl's useIntl() and these names. The API's values
// stay ISO (`yyyy-MM-dd`, `HH:MM`); only what is shown follows the salon's locale.
export const salonFormats = (currency: string): CustomFormats => ({
  number: { money: { style: "currency", currency } },
  date: {
    long: { dateStyle: "full" },
    short: { dateStyle: "short" },
    day: DAY,
  },
  time: { clock: CLOCK, hour: { hour: "numeric" } },
  dateTimeRange: { clock: CLOCK, day: DAY, short: { dateStyle: "short" } },
  duration: { short: { style: "short" } },
});

interface SalonIntlProviderProps {
  children: React.ReactNode;
}

export function SalonIntlProvider({ children }: SalonIntlProviderProps) {
  const { locale, currency } = useSalon();
  useSalonDateFnsLocale(); // loaded with the salon, so no page waits for it when it first shows a date picker
  const formats = useMemo(() => salonFormats(currency), [currency]);
  return (
    <IntlProvider locale={locale} formats={formats}>
      {children}
    </IntlProvider>
  );
}
